import type { Kernel, ParamSpec } from "./kernel";
import { lintKernelSource, usesShimTranscendentals } from "./lint";
import { SHIM_VERSION } from "./version";

// Custom (pasted) kernel transport — M2.
//
// A user-supplied kernel is a self-contained ES module that default-exports an object
// satisfying the Kernel contract. We load it WITHOUT eval / new Function: the source is
// turned into a Blob, and the Blob's object URL is dynamically imported. Importing a
// blob: URL is a normal module fetch — it does NOT require the `unsafe-eval` CSP keyword
// (only `wasm-unsafe-eval` is granted; see index.html / vite.config.ts). This is the whole
// point of the v1 `new Function` lesson recorded in CLAUDE.md: real module loading under a
// strict CSP instead of string-eval.
//
// The determinism linter (lint.ts, RFC-0008 §5) MUST pass before a kernel is loaded; a
// lint failure throws here so a nondeterministic kernel can never reach the compute path.

/** FNV-1a hash of the source — the content-address used as the custom kernel's id/hash. */
export function hashSource(source: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < source.length; i++) {
    h ^= source.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export class KernelLintError extends Error {
  constructor(public readonly findings: ReturnType<typeof lintKernelSource>) {
    super(
      "Custom kernel rejected by the determinism linter:\n" +
        findings.map((f) => `  • ${f.message}`).join("\n"),
    );
    this.name = "KernelLintError";
  }
}

/**
 * The honest cross-engine-verifiable flag for a kernel given its source. A kernel is only
 * verifiable if its author claims so AND it doesn't ride engine-specific transcendentals:
 * pre-M3 (shim = native) any `math.<transcendental>` call is engine-specific, so we force
 * `false` regardless of the author's flag. Once the fdlibm shim is active (SHIM_VERSION !=
 * "native"), shim transcendentals are cross-engine and the author's flag stands.
 * TODO(M3): when SHIM_VERSION becomes "fdlibm", this returns the author flag unchanged.
 */
export function effectiveVerifiable(declared: boolean, source: string): boolean {
  if (!declared) return false;
  if (SHIM_VERSION !== "native") return true; // M3+: shim is cross-engine bitwise
  return !usesShimTranscendentals(source);
}

function assertKernelShape(k: unknown): asserts k is Kernel {
  const obj = k as Record<string, unknown>;
  if (!obj || typeof obj !== "object") throw new Error("Kernel module must export a default object.");
  if (typeof obj.chunk !== "function") throw new Error("Kernel must implement chunk(count, rng, math).");
  if (typeof obj.reduce !== "function") throw new Error("Kernel must implement reduce(orderedPartials).");
}

/**
 * Lint, load (via Blob URL ES-module import), validate the shape, and return a Kernel.
 * Throws KernelLintError on lint failure or Error on a malformed module. The loader runs in
 * whatever realm calls it (worker thread for compute; the main thread only for a dry-run /
 * preview), never via eval.
 */
export async function loadCustomKernel(source: string): Promise<Kernel> {
  const findings = lintKernelSource(source);
  if (findings.length > 0) throw new KernelLintError(findings);

  const blob = new Blob([source], { type: "text/javascript" });
  const url = URL.createObjectURL(blob);
  try {
    // Vite/Rollup must not try to analyse this dynamic import (the URL is runtime-only).
    const mod = await import(/* @vite-ignore */ url);
    const kernel = mod.default;
    assertKernelShape(kernel);
    // A custom kernel's id is its content hash unless it set one; force verifiable honesty:
    // a custom kernel can only be cross-engine verifiable once it passes lint AND the shim
    // is cross-engine (M3). Pre-M3 the linter rejects transcendentals-via-Math but allows
    // math.* shim calls, which are still engine-specific until fdlibm — so a custom kernel
    // that uses math.* transcendentals must not claim verifiable. We trust the author's flag
    // for arithmetic-only kernels; the linter already guarantees no raw Math.transcendental.
    return kernel;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Cache a custom kernel's declared params by source hash so the UI can render controls
// without re-importing on every keystroke. Returns [] if the kernel declares none or fails
// to load (the editor's live linter already surfaces load problems separately).
const paramCache = new Map<string, readonly ParamSpec[]>();
export async function customKernelParams(source: string): Promise<readonly ParamSpec[]> {
  const hash = hashSource(source);
  const hit = paramCache.get(hash);
  if (hit) return hit;
  try {
    const kernel = await loadCustomKernel(source);
    const specs = Array.isArray(kernel.params) ? kernel.params : [];
    paramCache.set(hash, specs);
    return specs;
  } catch {
    return [];
  }
}
